/**
 * 坐标转换工具：WGS-84（GPS）→ GCJ-02（高德/火星坐标）
 *
 * 背景：车辆设备上报的 GPS 和浏览器 geolocation 定位均为 WGS-84 坐标系，
 * 而高德地图/高德 URI 使用 GCJ-02（火星坐标），直接使用会导致几百米定位偏差。
 * 本工具为国内通用转换算法（仅中国大陆境内有偏移，境外返回原值）。
 */

const PI = Math.PI;
const A = 6378245.0; // 长半轴
const EE = 0.00669342162296594323; // 偏心率平方

const transformLat = (x, y) => {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(y * PI) + 40.0 * Math.sin(y / 3.0 * PI)) * 2.0 / 3.0;
    ret += (160.0 * Math.sin(y / 12.0 * PI) + 320 * Math.sin(y * PI / 30.0)) * 2.0 / 3.0;
    return ret;
};

const transformLng = (x, y) => {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(x * PI) + 40.0 * Math.sin(x / 3.0 * PI)) * 2.0 / 3.0;
    ret += (150.0 * Math.sin(x / 12.0 * PI) + 300.0 * Math.sin(x / 30.0 * PI)) * 2.0 / 3.0;
    return ret;
};

/**
 * WGS-84 → GCJ-02
 * @param {number} lng 经度
 * @param {number} lat 纬度
 * @returns {{lng: number, lat: number}}
 */
export const wgs84ToGcj02 = (lng, lat) => {
    // 中国大陆范围外不做偏移（海外坐标偏移无意义）
    if (lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271) {
        return { lng, lat };
    }

    let dLat = transformLat(lng - 105.0, lat - 35.0);
    let dLng = transformLng(lng - 105.0, lat - 35.0);
    const radLat = lat / 180.0 * PI;
    let magic = Math.sin(radLat);
    magic = 1 - EE * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((A * (1 - EE)) / (magic * sqrtMagic) * PI);
    dLng = (dLng * 180.0) / (A / sqrtMagic * Math.cos(radLat) * PI);

    return {
        lng: lng + dLng,
        lat: lat + dLat
    };
};

/**
 * GCJ-02 → WGS-84（如需逆转换，当前业务未用到，预留）
 */
export const gcj02ToWgs84 = (lng, lat) => {
    const gcj = wgs84ToGcj02(lng, lat);
    return {
        lng: lng * 2 - gcj.lng,
        lat: lat * 2 - gcj.lat
    };
};

const X_PI = PI * 3000.0 / 180.0; // 百度坐标系圆周参数

/**
 * GCJ-02 → BD-09（高德/火星坐标 → 百度坐标）
 * @param {number} lng 经度（GCJ-02）
 * @param {number} lat 纬度（GCJ-02）
 * @returns {{lng: number, lat: number}} BD-09 坐标
 */
export const gcj02ToBd09 = (lng, lat) => {
    const x = lng;
    const y = lat;
    const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * X_PI);
    const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * X_PI);
    return {
        lng: z * Math.cos(theta) + 0.0065,
        lat: z * Math.sin(theta) + 0.006
    };
};

/**
 * BD-09 → GCJ-02（百度坐标 → 高德/火星坐标，预留）
 */
export const bd09ToGcj02 = (lng, lat) => {
    const x = lng - 0.0065;
    const y = lat - 0.006;
    const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI);
    const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI);
    return {
        lng: z * Math.cos(theta),
        lat: z * Math.sin(theta)
    };
};

/**
 * WGS-84 → BD-09（GPS → 百度坐标，一步到位）
 */
export const wgs84ToBd09 = (lng, lat) => {
    const gcj = wgs84ToGcj02(lng, lat);
    return gcj02ToBd09(gcj.lng, gcj.lat);
};
